import { Form } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { MassActionsDropdown } from '@app/components';
import {
  DATE_FORMATS,
  FRONT_WARNING,
  RIGHTS_CRM_TASKS,
  RIGHTS_IS_VIEW_ALL,
  TASK_LIST_PATH,
} from '@app/constants';
import { useAccessRights, useUserPrint } from '@app/hooks';
import { TaskTypeOptionLabel } from '@app/pages/TasksList/components/TaskTypeOptionLabel';
import { PERIOD_FILTER_DEFAULT_VALUE } from '@app/pages/TasksList/constants/registryFiltersDefaultKeys';
import {
  TODAY,
  TOMORROW,
  UPCOMING,
  EXPIRED,
} from '@app/pages/TasksList/constants/registryFilterTypes';
import {
  DEFAULT_PAGE,
  DEFAULT_ROWS_PER_PAGE,
} from '@app/pages/TasksList/constants/registryTablePagination';
import { TableMassActionItems } from '@app/pages/TasksList/data/TableMassActionItems';
import type { NTasksList } from '@app/pages/TasksList/types/TasksList.types';
import { adaptDataToTable } from '@app/pages/TasksList/utils/adaptDataToTable';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import {
  EXPIRED_STATUS_INDEX,
  getAYearAfterTodayDate,
  getTodayDate,
  getTomorrowDate,
} from '@app/providers/TaskListProvider/constants';
import type { OpenDeleteTasksModalHandlerType } from '@app/providers/TaskListProvider/hooks/useDeleteTasksModal';
import type { NTaskListProvider } from '@app/providers/TaskListProvider/types';
import {
  clearSession,
  readSession,
  writeSession,
} from '@app/providers/TaskListProvider/utils/sessionUtils';
import {
  useGetCrmTaskFilterDataQuery,
  useLazyGetSearchCrmTasksQuery,
  useSearchCrmTasksMutation,
} from '@app/services/rtkQuery/CrmTasks/api';
import type { NCrmTasks } from '@app/services/rtkQuery/CrmTasks/types';
import type { NTables } from '@app/types';
import { getSelectOptionsWithDeletedItems, getStatusColor } from '@app/utils';

import type { BaseOptionType } from 'antd/es/select';
import type { TableRowSelection } from 'antd/es/table/interface';
import type { TablePaginationConfig } from 'antd/lib';
import type { Dispatch, Key, ReactNode } from 'react';

const { useForm, useWatch } = Form;
interface UseTaskRegistry {
  selectedEmployee: BaseOptionType[];
  setSelectedEmployee: Dispatch<BaseOptionType[]>;
  rightToDeleteTask: boolean;
  rightToEditTask: boolean;
  rightToEditAllTask: boolean;
  rightToCreateTask: boolean;
  clientsSearchTerm?: string;
  openChangeEmployeeModal(): void;
  openFinishTaskModal(): void;
  openDeleteTasksModal: OpenDeleteTasksModalHandlerType;
  openEditTaskModal(task: NCrmTasks.ModelCrmTask): void;
  selectTaskForAction(target: number | NCrmTasks.ModelCrmTask): void;
  openPreviewTaskModal(id: number): void;
  activeTab: NTaskListProvider.Tab;
}
export const useTaskRegistry = ({
  selectedEmployee,
  setSelectedEmployee,
  rightToEditTask,
  rightToEditAllTask,
  rightToCreateTask,
  rightToDeleteTask,
  openFinishTaskModal,
  openDeleteTasksModal,
  openChangeEmployeeModal,
  openEditTaskModal,
  selectTaskForAction,
  openPreviewTaskModal,
  activeTab,
}: UseTaskRegistry) => {
  const {
    activeBranchId: ActiveBranchId,
    userId,
    database,
    user: currentUser,
  } = useUserPrint();
  const { showNotification } = useNotification();
  const [taskList, setTaskList] = useState<Array<NCrmTasks.ModelCrmTask>>([]);
  const [taskListWithPagination, setTaskListWithPagination] =
    useState<NCrmTasks.ModelCrmTaskSearchResult>();
  const [selectedRowKeys, setSelectedRowKeys] = useState<Array<Key>>([]);

  const [statusesFilterList, setStatusesFilterList] = useState<
    BaseOptionType[]
  >([]);
  const [periodTypeFilterList, setPeriodTypeFilterList] = useState<
    BaseOptionType[]
  >([]);
  const [ownersFilterList, setOwnersFilterList] = useState<BaseOptionType[]>(
    []
  );
  const [creatorsFilterList, setCreatorsFilterList] = useState<
    BaseOptionType[]
  >([]);
  const [branchesFilterList, setBranchesFilterList] = useState<
    BaseOptionType[]
  >([]);
  const [taskTypesFilterList, setTaskTypesFilterList] = useState<
    BaseOptionType[]
  >([]);

  const [selectedStatuses, setSelectedStatuses] = useState<BaseOptionType[]>(
    []
  );
  const [selectedCreators, setSelectedCreators] = useState<BaseOptionType[]>(
    []
  );
  const [selectedBranches, setSelectedBranches] = useState<BaseOptionType[]>(
    []
  );
  const [selectedTaskTypes, setSelectedTaskTypes] = useState<BaseOptionType[]>(
    []
  );
  //  Форма фильтров для страницы Задачи: Реестр - Тип периода, Период и Поиск.
  const [tableHeaderForm] = useForm<NTasksList.TableHeaderFormType>();
  const tableHeaderFormValues = useWatch([], tableHeaderForm);

  const saveFiltersToSession = useCallback(() => {
    const {
      Search = '',
      PeriodType,
      Date,
    } = tableHeaderFormValues || {
      Search: '',
      PeriodType: undefined,
      Date: undefined,
    };
    const filtersToPersist: NTaskListProvider.PersistedTaskRegistryFilters = {
      Search: Search || '',
      PeriodType: PeriodType || PERIOD_FILTER_DEFAULT_VALUE,
      DateS: Date
        ? Date?.[0]?.format(DATE_FORMATS.ISO_FORMAT_NO_TIMEZONE) || ''
        : '',
      DateE: Date
        ? Date?.[1]?.format(DATE_FORMATS.ISO_FORMAT_NO_TIMEZONE) || ''
        : '',
      OwnerIds: selectedEmployee.map(({ value }) => value),
      CreatorIds: selectedCreators.map(({ value }) => value),
      BranchIds: selectedBranches.map(({ value }) => value),
      Statuses: selectedStatuses.map(({ value }) => value),
      page: taskListWithPagination?.Page || DEFAULT_PAGE,
      rowPerPage: taskListWithPagination?.RowsPerPage || DEFAULT_ROWS_PER_PAGE,
    };

    writeSession(filtersToPersist);
  }, [
    tableHeaderFormValues,
    selectedEmployee,
    selectedCreators,
    selectedBranches,
    selectedStatuses,
    taskListWithPagination,
  ]);

  useEffect(() => {
    saveFiltersToSession();
  }, [saveFiltersToSession]);

  const [isFirstRegistryLoad, setIsFirstRegistryLoad] = useState(true);
  const hasAppliedInitialFiltersRef = useRef(false);

  const { hasUserRight: hasUserRightToViewOtherOwnersTasks } = useAccessRights(
    RIGHTS_CRM_TASKS,
    RIGHTS_IS_VIEW_ALL
  );

  const { pathname } = useLocation();
  const isTaskList = useMemo(() => pathname === TASK_LIST_PATH, [pathname]);
  const isTaskRegistry = useMemo(() => activeTab === 'registry', [activeTab]);

  const [searchCrmTasks, { isLoading: isCreateStageLoading }] =
    useSearchCrmTasksMutation();

  const {
    data: selectorFilterData,
    error: filtersError,
    isLoading: isFilterDataLoading,
    refetch: refetchTaskFiltersQuery,
  } = useGetCrmTaskFilterDataQuery(
    { ActiveBranchId, logs: { isLogged: true, userId, database } },
    { skip: !ActiveBranchId || !isTaskList }
  );

  // The query above is skipped outside /tasks (e.g. creating a task from a
  // lead card), where RTK Query's refetch() throws ("Cannot refetch a query
  // that has not been started yet"). Guard it the same way the query itself
  // is skipped.
  const refetchTaskFilters = useCallback(() => {
    if (!ActiveBranchId || !isTaskList) return;
    refetchTaskFiltersQuery();
  }, [ActiveBranchId, isTaskList, refetchTaskFiltersQuery]);

  const [handleTaskListPagination, { isLoading: isPaginationDataLoading }] =
    useLazyGetSearchCrmTasksQuery();
  handleTaskListPagination;
  const isRegistryTableLoading = useMemo(
    () =>
      isCreateStageLoading || isFilterDataLoading || isPaginationDataLoading,
    [isCreateStageLoading, isFilterDataLoading, isPaginationDataLoading]
  );

  const getRightToEditTask = useCallback(
    (id?: number): boolean => {
      const currentTask = taskList.find(({ Id }) => Id === id);
      const taskOwnersIds =
        currentTask?.Owners?.map((item) => item.UserId) || [];
      if (userId === currentTask?.UserCreateId && rightToCreateTask) {
        return true;
      } else if (rightToEditTask && userId && taskOwnersIds.includes(userId)) {
        return true;
      } else if (rightToEditAllTask) {
        return true;
      } else {
        return false;
      }
    },
    [taskList, userId, rightToEditTask, rightToCreateTask, rightToEditAllTask]
  );

  const setInitialEmployee = useCallback(
    (ownersFilterList: BaseOptionType[]) => {
      const initialEmployee = ownersFilterList.find(
        (item) => item.value === userId
      );
      if (initialEmployee) {
        setSelectedEmployee([initialEmployee]);
      }
    },
    [setSelectedEmployee, userId]
  );

  const setDefaultFiltersData = useCallback(() => {
    tableHeaderForm.setFieldsValue({
      PeriodType: PERIOD_FILTER_DEFAULT_VALUE,
      Search: '',
      Date: undefined,
    });
    setInitialEmployee(ownersFilterList);
    setSelectedCreators([]);
    setSelectedStatuses([]);
    setSelectedBranches([]);
    setSelectedTaskTypes([]);
    clearSession();
  }, [tableHeaderForm, setInitialEmployee, ownersFilterList]);

  //  Функция для получения тела запроса для поиска задачи Реестра:
  const getSearchTaskRequestBody = useCallback(
    (isDefaultRequestBody?: boolean): NCrmTasks.ModelCrmTaskSearchRequest => {
      const { Search, Date, PeriodType } = tableHeaderForm.getFieldsValue();
      const DateS =
        Date?.[0]?.format(DATE_FORMATS.ISO_FORMAT_NO_TIMEZONE) || null;
      const DateE =
        Date?.[1]?.format(DATE_FORMATS.ISO_FORMAT_NO_TIMEZONE) || null;

      switch (isDefaultRequestBody) {
        case true:
          return {
            page: DEFAULT_PAGE,
            rowPerPage: DEFAULT_ROWS_PER_PAGE,
            ActiveBranchId,
            data: {
              DateS: null,
              DateE: null,
              OwnerIds: [userId].filter(
                (value) => value !== undefined && value !== null
              ),
              SearchTerm: null,
              CreatorIds: null,
              ClientIds: null,
              BranchIds: null,
              TaskTypeIds: null,
              Statuses: null,
              PeriodType: PERIOD_FILTER_DEFAULT_VALUE,
            },
          };
        default:
          return {
            page: 1,
            rowPerPage: 10,
            ActiveBranchId,
            data: {
              DateS,
              DateE,
              ClientIds: null,
              SearchTerm: Search || null,
              OwnerIds: (isFirstRegistryLoad
                ? [userId]
                : selectedEmployee.map(({ value }) => value) || null
              ).filter((value) => value !== undefined && value !== null),
              CreatorIds: selectedCreators.map(({ value }) => value) || null,
              BranchIds: selectedBranches.map(({ value }) => value) || null,
              TaskTypeIds: selectedTaskTypes.map(({ value }) => value) || null,
              Statuses: selectedStatuses.map(({ value }) => value) || null,
              PeriodType,
            },
          };
      }
    },
    [
      tableHeaderForm,
      ActiveBranchId,
      isFirstRegistryLoad,
      userId,
      selectedEmployee,
      selectedCreators,
      selectedBranches,
      selectedTaskTypes,
      selectedStatuses,
    ]
  );

  //  Функция для фетча списка задач Реестра
  const fetchTasks: NCrmTasks.FetchListData = useCallback(
    async (requestData) => {
      if (!isTaskList) return;
      try {
        const data = await searchCrmTasks(requestData).unwrap();
        const taskList = data.Rows ?? [];

        setTaskList(taskList);
        setTaskListWithPagination(data);
      } catch (error) {
        showNotification(error);
      }
    },
    [searchCrmTasks, showNotification, isTaskList]
  );

  //  Функция для фетча списка задач Реестра по выбранным фильтрам
  const fetchTaskList = useCallback(() => {
    fetchTasks(getSearchTaskRequestBody());
  }, [fetchTasks, getSearchTaskRequestBody]);

  //  Функция для фетча списка задач Реестра по дефолтным фильтрам
  const fetchDefaultTaskList = useCallback(() => {
    fetchTasks(getSearchTaskRequestBody(true));
  }, [fetchTasks, getSearchTaskRequestBody]);

  const handleReset = useCallback(() => {
    fetchDefaultTaskList();
    setDefaultFiltersData();
  }, [setDefaultFiltersData, fetchDefaultTaskList]);

  // Функция вызывается при клике на кнопку Поиск на странице Задачи (Реестр)
  const searchTasks = useCallback(() => {
    fetchTaskList();
  }, [fetchTaskList]);

  //  Функция для выбора группы фильтров - Сегодня, Завтра, Предстоящие, Просроченные
  const selectFilterGroup = useCallback(
    async (filterType: string) => {
      const requestBody = getSearchTaskRequestBody(true);
      setDefaultFiltersData();
      const getRequestBody = (filterType: string) => {
        const todayDate = getTodayDate();
        const tomorrowDate = getTomorrowDate();
        const formatDate = (date: typeof todayDate) =>
          date.format(DATE_FORMATS.ISO_FORMAT_NO_TIMEZONE);

        switch (filterType) {
          case EXPIRED:
            const expiredStatus = statusesFilterList.find(
              ({ value }) => value === EXPIRED_STATUS_INDEX
            ) || { value: EXPIRED_STATUS_INDEX, label: 'Просрочена' };
            setSelectedStatuses([expiredStatus]);
            return {
              ...requestBody,
              data: {
                ...requestBody.data,
                Statuses: [EXPIRED_STATUS_INDEX],
              },
            };
          case TODAY:
            tableHeaderForm.setFieldValue('Date', [todayDate, todayDate]);
            return {
              ...requestBody,
              data: {
                ...requestBody.data,
                DateS: formatDate(todayDate),
                DateE: formatDate(todayDate),
              },
            };
          case TOMORROW:
            tableHeaderForm.setFieldValue('Date', [tomorrowDate, tomorrowDate]);
            return {
              ...requestBody,
              data: {
                ...requestBody.data,
                DateS: formatDate(tomorrowDate),
                DateE: formatDate(tomorrowDate),
              },
            };
          case UPCOMING:
            const aYearAfterTodayDate = getAYearAfterTodayDate();
            tableHeaderForm.setFieldValue('Date', [
              tomorrowDate,
              aYearAfterTodayDate,
            ]);
            return {
              ...requestBody,
              data: {
                ...requestBody.data,
                DateS: formatDate(tomorrowDate),
                DateE: formatDate(aYearAfterTodayDate),
              },
            };
          default:
            return requestBody;
        }
      };
      fetchTasks(getRequestBody(filterType));
    },
    [
      fetchTasks,
      getSearchTaskRequestBody,
      statusesFilterList,
      tableHeaderForm,
      setDefaultFiltersData,
    ]
  );

  const filtersHandler = useCallback(
    ({
      Statuses,
      PeriodTypes,
      Owners,
      Creators,
      Branches,
      TaskTypes,
    }: NCrmTasks.ModelCrmTaskFilterData) => {
      const statusesFilterList = (Statuses ?? []).map(
        ({ Status, StatusDescription }) => ({
          value: Status,
          label: StatusDescription || '',
        })
      );

      const periodTypeFilterList = (PeriodTypes ?? []).map(
        ({ Status, StatusDescription }) => ({
          value: Status,
          label: StatusDescription || '',
        })
      );

      const currentUserLabel = `${currentUser?.nameLast || ''} ${
        currentUser?.nameFirst || ''
      }`.trim();

      const currentUserForFilters = {
        value: currentUser?.userId!,
        label: currentUserLabel,
        IsDeleted: false,
        filterName: currentUserLabel,
      };

      const mappedOwners = (Owners ?? []).map(
        ({ UserId, NameLast, NameFirst, IsDeleted }) => {
          const label = `${NameLast || ''} ${NameFirst || ''}`.trim();

          return {
            value: UserId,
            label,
            IsDeleted,
            filterName: label,
          };
        }
      );

      const isCurrentUserExists = mappedOwners.some(
        (owner) => owner.value === currentUser?.userId
      );

      const ownersFilterList = isCurrentUserExists
        ? mappedOwners
        : [currentUserForFilters, ...mappedOwners];

      const creatorsFilterList = (Creators ?? []).map(
        ({ UserId, NameLast, NameFirst, IsDeleted }) => {
          const label = `${NameLast || ''} ${NameFirst || ''}`.trim();

          return {
            value: UserId,
            label,
            IsDeleted,
            filterName: label,
          };
        }
      );
      const branchesFilterList = (Branches ?? []).map(
        ({ Id, Name, IsDeleted }) => {
          const label = `${Name || ''}`.trim();

          return {
            value: Id,
            label,
            IsDeleted,
            filterName: label,
          };
        }
      );

      const taskTypesFilterList = (TaskTypes ?? []).map(
        ({ Id, Name, IconId, Color }) => ({
          value: Id,
          // Plain string kept for search/filtering (filterName="searchLabel");
          // label carries the icon + name node for the dropdown and chips.
          searchLabel: Name || '',
          label: (
            <TaskTypeOptionLabel
              name={Name || ''}
              iconId={IconId}
              color={Color}
            />
          ),
        })
      );

      setCreatorsFilterList(
        getSelectOptionsWithDeletedItems(creatorsFilterList)
      );
      setBranchesFilterList(
        getSelectOptionsWithDeletedItems(branchesFilterList)
      );
      setTaskTypesFilterList(taskTypesFilterList);

      const withoutOwnerElement = {
        value: 0,
        label: 'Без ответственного',
        IsDeleted: false,
        filterName: 'label',
      };

      const baseOwners = hasUserRightToViewOtherOwnersTasks
        ? ownersFilterList
        : ownersFilterList.filter(({ value }) => value === userId);

      const ownerFilterList = getSelectOptionsWithDeletedItems([
        withoutOwnerElement,
        ...baseOwners,
      ]);
      setOwnersFilterList(ownerFilterList);

      setPeriodTypeFilterList(periodTypeFilterList);
      setStatusesFilterList(statusesFilterList);

      // Below this point: one-time initialization of the *selected* filters
      // (default owner + session restore), not the option lists rebuilt
      // above. filtersHandler can run again later (e.g. a filter-data
      // refetch after creating a task) purely to refresh the option lists -
      // re-running the initialization would stomp on filters the user has
      // since changed, so it only ever runs once.
      if (hasAppliedInitialFiltersRef.current) return;
      hasAppliedInitialFiltersRef.current = true;

      setInitialEmployee(ownersFilterList);

      //Инициализация фильтров из сессии при первом заходе на страницу Реестра
      const filtersFromSession = readSession();
      if (filtersFromSession) {
        tableHeaderForm.setFieldsValue({
          Search: filtersFromSession.Search || '',
          PeriodType: filtersFromSession.PeriodType,
          Date:
            filtersFromSession.DateS && filtersFromSession.DateE
              ? [
                  dayjs(filtersFromSession.DateS),
                  dayjs(filtersFromSession.DateE),
                ]
              : undefined,
        });
        setSelectedEmployee(
          ownerFilterList.filter(
            (owner) =>
              filtersFromSession.OwnerIds.includes(owner.value) ||
              owner.value === userId
          )
        );
        setSelectedCreators(
          creatorsFilterList.filter((creator) =>
            filtersFromSession.CreatorIds.includes(creator.value)
          )
        );
        setSelectedBranches(
          branchesFilterList.filter((branch) =>
            filtersFromSession.BranchIds.includes(branch.value)
          )
        );
        setSelectedStatuses(
          statusesFilterList.filter((status) =>
            filtersFromSession.Statuses.includes(status.value)
          )
        );
      }
    },
    [
      currentUser?.nameLast,
      currentUser?.nameFirst,
      currentUser?.userId,
      hasUserRightToViewOtherOwnersTasks,
      setInitialEmployee,
      userId,
      tableHeaderForm,
      setSelectedEmployee,
    ]
  );

  const handlePageChange = useCallback(
    async (page?: number, rowPerPage?: number) => {
      if (!taskListWithPagination) return;
      try {
        const data = await handleTaskListPagination({
          searchId: taskListWithPagination.Id,
          page: page || taskListWithPagination.Page,
          rowPerPage: rowPerPage || taskListWithPagination.RowsPerPage,
          ActiveBranchId,
        }).unwrap();

        const taskList = data.Rows ?? [];
        setTaskList(taskList);
        setTaskListWithPagination(data);
      } catch (error) {
        showNotification(error);
      }
    },
    [
      handleTaskListPagination,
      taskListWithPagination,
      ActiveBranchId,
      showNotification,
    ]
  );

  const openEditTaskModalHandler = useCallback(
    (selectTaskId: number) => {
      const task = taskList.find(({ Id }) => Id === selectTaskId);

      if (!task)
        return showNotification(
          'При загрузке информации по задаче возникла ошибка',
          FRONT_WARNING
        );

      selectTaskForAction(task);
      openEditTaskModal(task);
    },
    [openEditTaskModal, taskList, selectTaskForAction, showNotification]
  );

  // Registry row order: primary by deadline (earlier deadline higher), then by
  // start time (earlier start higher) when deadlines are equal. Backend already
  // returns both TimeStart and TimeDeadline, so the ordering is applied on the
  // fetched page.
  const sortedTaskList = useMemo(
    () =>
      [...taskList].sort((first, second) => {
        const deadlineDiff =
          dayjs(first.TimeDeadline).valueOf() -
          dayjs(second.TimeDeadline).valueOf();

        if (deadlineDiff !== 0) return deadlineDiff;

        return (
          dayjs(first.TimeStart).valueOf() - dayjs(second.TimeStart).valueOf()
        );
      }),
    [taskList]
  );

  const tableData: NTables.TaskListHeader[] = useMemo(
    () =>
      adaptDataToTable({
        data: sortedTaskList,
        handleEdit: openEditTaskModalHandler,
        handlePreviewIconClick: openPreviewTaskModal,
        getRightToEditTask,
      }),
    [
      sortedTaskList,
      getRightToEditTask,
      openEditTaskModalHandler,
      openPreviewTaskModal,
    ]
  );

  const onSelectChange = useCallback((newSelectedRowKeys: Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  }, []);

  const rowSelection: TableRowSelection<NTables.TaskListHeader> = useMemo(
    () => ({
      type: 'checkbox',
      renderCell: (
        value: boolean,
        record: NTables.TaskListHeader,
        index: number,
        originNode: ReactNode
      ) => (
        <div
          className="checkbox-container"
          style={{
            borderLeft: `6px solid ${getStatusColor(
              record.Status.props.status
            )}`,
            height: '100px',
          }}
        >
          {originNode}
        </div>
      ),
      selectedRowKeys,
      onChange: onSelectChange,
      preserveSelectedRowKeys: true,
    }),
    [selectedRowKeys, onSelectChange]
  );

  const pagination: TablePaginationConfig = useMemo(
    () => ({
      size: 'default',
      current: taskListWithPagination?.Page,
      showSizeChanger: true,
      total: taskListWithPagination?.TotalCount,
      pageSize: taskListWithPagination?.RowsPerPage,
      position: ['bottomLeft'],
      onChange: handlePageChange,
      showTotal: (total, [start, end]) =>
        `Показаны ${start}-${end} из ${total}`,
    }),
    [
      taskListWithPagination?.Page,
      taskListWithPagination?.RowsPerPage,
      taskListWithPagination?.TotalCount,
      handlePageChange,
    ]
  );

  const massActionItems = TableMassActionItems({
    selectionCount: selectedRowKeys.length,
    openChangeEmployeeModal,
    openFinishTaskModal,
    openDeleteTasksModal,
    rightToEditTask,
    rightToDeleteTask,
  });

  const MassActionButton =
    taskList.length > 0 ? (
      <div className="mass-actions">
        <MassActionsDropdown
          items={massActionItems}
          data={taskList}
          selectionCount={selectedRowKeys.length}
        />
      </div>
    ) : (
      <></>
    );

  const skipSelectedRows = useCallback(() => {
    setSelectedRowKeys([]);
  }, []);

  useEffect(() => {
    if (selectorFilterData) {
      filtersHandler(selectorFilterData);
    }
    // filtersHandler в зависимостях вызывает бесконечный рендер
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectorFilterData]);

  useEffect(() => {
    if (isTaskRegistry) {
      if (!ActiveBranchId) return;
      if (!userId) return;
      // При самом первом заходе форма фильтров (в частности, PeriodType)
      // ещё не инициализирована значениями по умолчанию - selectorFilterData
      // подгружается асинхронно и позже. Поэтому для первого запроса
      // используется явное дефолтное тело запроса (как при "Сбросе"), а не
      // getSearchTaskRequestBody() с пустыми/неинициализированными полями формы.
      if (isFirstRegistryLoad) {
        fetchDefaultTaskList();
      } else {
        fetchTaskList();
      }
      setIsFirstRegistryLoad(false);
    }
    // убран fetchTaskList, т.к. вызывает лишние запросы
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTaskRegistry, ActiveBranchId, userId]);

  useEffect(() => {
    if (filtersError) {
      showNotification(filtersError, FRONT_WARNING);
    }
  }, [filtersError, showNotification]);

  return {
    tableHeaderForm,
    handleReset,
    selectedStatuses,
    setSelectedStatuses,
    selectedCreators,
    setSelectedCreators,
    selectedBranches,
    setSelectedBranches,
    searchTasks,
    selectorFilterData,
    selectFilterGroup,
    periodTypeFilterList,
    statusesFilterList,
    ownersFilterList,
    creatorsFilterList,
    fetchTaskList,
    refetchTaskFilters,
    taskList,
    isRegistryTableLoading,
    tableData,
    rowSelection,
    pagination,
    MassActionButton,
    selectedRowKeys,
    skipSelectedRows,
    branchesFilterList,
    handlePageChange,
    taskTypesFilterList,
    selectedTaskTypes,
    setSelectedTaskTypes,
  };
};
